import React, { useEffect, useState } from 'react';
import { fetchEspacioHistory } from '../../../services/espacioHistoryService';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { History } from 'lucide-react';

interface HistoryEntry {
    id: number;
    action: string;
    changes: any;
    user_email: string;
    user_full_name: string;
    espacio_name: string;
    timestamp: string;
}

const SpaceHistoryPage: React.FC = () => {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterUser, setFilterUser] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const itemsPerPage = 10;
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();
    const navigate = useNavigate();

    const loadHistory = async () => {
        try {
            const data = await fetchEspacioHistory();
            setHistory(data);
        } catch (error) {
            console.error('Error cargando historial:', error);
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    const filteredHistory = history.filter(entry => {
        const matchesUser = entry.user_full_name.toLowerCase().includes(filterUser.toLowerCase());
        const matchesAction = filterAction ? entry.action === filterAction : true;
        const entryDate = new Date(entry.timestamp);

        const matchesDate = () => {
            if (!startDate && !endDate) return true;
            const start = startDate ? new Date(startDate.setHours(0, 0, 0, 0)) : null;
            const end = endDate ? new Date(endDate.setHours(23, 59, 59, 999)) : null;

            if (start && end) return entryDate >= start && entryDate <= end;
            if (start) return entryDate >= start;
            if (end) return entryDate <= end;
            return true;
        };

        return matchesUser && matchesAction && matchesDate();
    });

    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedEntries = filteredHistory.slice(startIndex, startIndex + itemsPerPage);

    const renderChanges = (changes: any, action: string) => {
        if (!changes) return 'Sin detalles disponibles';

        const formatValue = (value: any) => {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Activo' : 'Inactivo';
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
                return new Date(value).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
            if (typeof value === 'string' && value.startsWith('espacios/')) {
                return <span className="text-blue-600">Archivo: {value.split('/').pop()}</span>;
            }
            return value?.toString() || 'N/A';
        };

        if (action === 'created' || action === 'deleted') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, value]) => (
                        <div key={key} className="flex gap-1">
                            <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                            <span className="text-gray-600">
                                {formatValue(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (action === 'updated') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, val]) => {
                        const value = val as { old: any; new: any };
                        return (
                            <div key={key} className="flex flex-col">
                                <span className="font-medium capitalize">{key.replace('_', ' ')}</span>
                                <div className="flex gap-2 text-sm">
                                    <span className="text-red-600 line-through">{formatValue(value.old)}</span>
                                    <span>→</span>
                                    <span className="text-green-600">{formatValue(value.new)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return 'Sin detalles disponibles';
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filterUser, filterAction, startDate, endDate]);

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />

            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Historial de Espacios</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/spaces" className="hover:text-blue-600">Espacios</a>
                        <span>/</span>
                        <a href="/spaces/history" className="text-[#c1348b] hover:text-[#8f1f64]">Historial</a>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/spaces')}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center text-sm font-medium"
                >
                    Volver a Espacios
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <input
                            type="text"
                            placeholder="Filtrar por usuario"
                            className="px-3 py-2 border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                        />

                        <select
                            className="px-3 py-2 border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                        >
                            <option value="">Todas las acciones</option>
                            <option value="created">Creación</option>
                            <option value="updated">Actualización</option>
                            <option value="deleted">Eliminación</option>
                        </select>

                        <DatePicker
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => {
                                const [start, end] = update as [Date | null, Date | null];
                                setStartDate(start);
                                setEndDate(end);
                            }}
                            isClearable={true}
                            placeholderText="Rango de fechas"
                            locale="es"
                            dateFormat="dd/MM/yyyy"
                            className="px-3 py-2 border rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            monthsShown={2}
                            showPopperArrow={false}
                            calendarClassName="shadow-lg"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Espacio</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cambios</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {displayedEntries.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <History className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay historial disponible
                                            </span>
                                            <p className="text-gray-500 text-sm">
                                                Realiza acciones en espacios para generar historial
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedEntries.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.user_full_name}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            {entry.user_email}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            {entry.espacio_name}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${entry.action === 'created' ? 'bg-green-100 text-green-800' :
                                                    entry.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-red-100 text-red-800'
                                                }`}>
                                                {entry.action === 'created' && 'Creación'}
                                                {entry.action === 'updated' && 'Actualización'}
                                                {entry.action === 'deleted' && 'Eliminación'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(entry.timestamp).toLocaleDateString('es-ES', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                            })}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                                            {renderChanges(entry.changes, entry.action)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredHistory.length}
                />
            </div>
        </div>
    );
};

export default SpaceHistoryPage;